import { useMemo } from "react";
import { ReactFlow, Background, Controls, Handle, Position } from "@xyflow/react";
import { fmtT, tlFormat } from "@/lib/calFormat";

function TimelineNode({ data }) {
  return (
    <div data-testid={`branch-node-${data.id}`}
      className={`rounded-md border px-4 py-3 w-56 cursor-pointer transition-colors ${data.active ? "border-[#D97706] bg-[#1A202C] shadow-[0_0_24px_rgba(217,119,6,.25)]" : "border-[#2D3442] bg-[#13171F] hover:border-[#D97706]/60"}`}>
      <Handle type="target" position={Position.Left} style={{ background: "#8B5CF6", border: 0 }} />
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#A8A090]">{data.isRoot ? "Root reality" : `Diverges at ${data.branch}`}</div>
      <div className="font-display text-xl text-[#F3EBDD] leading-tight mt-0.5">{data.name}</div>
      <div className="text-xs text-[#A8A090] mt-1">{data.count} event{data.count === 1 ? "" : "s"}</div>
      <Handle type="source" position={Position.Right} style={{ background: "#D97706", border: 0 }} />
    </div>
  );
}

const nodeTypes = { timeline: TimelineNode };

export const BranchTree = ({ timelines, activeId, onSelect, jumps = [] }) => {
  const { nodes, edges } = useMemo(() => {
    const children = {};
    timelines.forEach((t) => { const p = t.parent_timeline_id || "root"; (children[p] = children[p] || []).push(t); });
    const nodes = [], edges = [];
    let row = 0;
    const walk = (t, depth) => {
      nodes.push({ id: t.id, type: "timeline", position: { x: depth * 380, y: row * 120 },
        data: { id: t.id, name: t.name, count: t.event_count, branch: fmtT(tlFormat(timelines, t), t.branch_point_timestamp), isRoot: !t.parent_timeline_id, active: t.id === activeId } });
      const kids = (children[t.id] || []).sort((a, b) => (a.branch_point_timestamp ?? 0) - (b.branch_point_timestamp ?? 0));
      if (!kids.length) row += 1;
      kids.forEach((k) => {
        edges.push({ id: `${t.id}-${k.id}`, source: t.id, target: k.id, label: fmtT(tlFormat(timelines, k), k.branch_point_timestamp, true), labelBgPadding: [6, 3], animated: k.id === activeId,
          style: { stroke: "#8B5CF6" }, labelStyle: { fill: "#F3EBDD", fontSize: 11 }, labelBgStyle: { fill: "#13171F" } });
        walk(k, depth + 1);
      });
    };
    (children.root || []).forEach((t) => walk(t, 0));
    jumps.filter((j) => j.departure && j.arrival && j.departure.timeline_id !== j.arrival.timeline_id).forEach((j) => {
      edges.push({ id: `jump-${j.id}`, source: j.departure.timeline_id, target: j.arrival.timeline_id, type: "smoothstep", label: `⧗ ${j.traveller_name}`,
        style: { stroke: "#C4B5FD", strokeDasharray: "6 4" }, labelStyle: { fill: "#C4B5FD", fontSize: 11 }, labelBgStyle: { fill: "#13171F" }, markerEnd: { type: "arrowclosed", color: "#C4B5FD" } });
    });
    return { nodes, edges };
  }, [timelines, activeId, jumps]);

  return (
    <div className="h-[520px] rounded-md border border-[#2D3442] bg-[#0F131A]" data-testid="branch-tree">
      <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitView nodesDraggable={false} nodesConnectable={false}
        onNodeClick={(_, n) => onSelect(n.id)} proOptions={{ hideAttribution: true }} colorMode="dark">
        <Background color="#2D3442" gap={24} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};
